import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { BaseFaxMedicalRequestComposite } from "@/Common/Models/Faxing/BaseFaxMedicalRequestComposite";
import { FaxLabRequestComposite } from "@/Common/Models/Faxing/FaxLabRequestComposite";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { Guid } from "@/Common/Models/Guid";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { LabRequestType } from "@/Common/Models/Patient/LabRequest";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileDisplayOption, ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";
import { FaxConsultationRequestComposite } from "@/ExportableFile/Models/FaxConsultationRequestComposite";
import { FileTypeConsultationRequest } from "@/ExportableFile/Models/FileTypes/FileTypeConsultationRequest";
import { FileTypeExamRequest } from "@/ExportableFile/Models/FileTypes/FileTypeExamRequest";
import { MedicalItemScreeningTest, MedicalItemScreeningTestWithLink } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { IFaxLabRequestComposite } from "@/Patient/Stores/Actions/PatientMedicalNoteActions";
import { LoadConsultationResponse } from "@/Portfolio/Api/Dto/Responses/LoadConsultationResponse";
import { LoadLabResponse } from "@/Portfolio/Api/Dto/Responses/LoadLabResponse";
import { SaveConsultationResponse } from "@/Portfolio/Api/Dto/Responses/SaveConsultationResponse";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";
import { LabRequest as MyleModelsPortfolioLabRequest } from "@/Portfolio/Models/LabRequest";
import { RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { PrintInfo } from "@/Portfolio/Models/PrintInfo";

type MedicalRequestActionLoadedCallback = (
    item: IMedicalRequest,
    printInfo?: PrintInfo,
    linkedElement?: MedicalItemScreeningTestWithLink,
    attachedDocuments?: ExportableFileModel[]
) => void;

interface IMedicalRequestAction {
    load(id: string, includePatientInfo?: boolean, success?: MedicalRequestActionLoadedCallback, error?: (message: ErrorMessage[]) => void): void;

    save(
        item: IMedicalRequest,
        linkingRequest?: LinkingCareElementRequest,
        comment?: string,
        shouldNotUpdateMedicalRequest?: boolean,
        patientAttachments?: AttachedObjectResponse[],
        success?: (response: IMedicalRequest, shouldSaveRelatedEmployee: boolean, printInfo?: PrintInfo) => void,
        error?: (message: ErrorMessage[]) => void
    ): Promise<void>;

    delete(
        id: Guid,
        encounterId: Guid,
        success?: (requestId: Guid, encounterId: Guid) => void,
        error?: (message: ErrorMessage[]) => void
    ): void;

    invalidate(
        id: Guid,
        encounterId: Guid,
        isInvalid: boolean,
        sharedItemId?: string,
        success?: (id: Guid, encounterId: Guid, isInvalid: boolean) => void,
        error?: (message: ErrorMessage[]) => void
    ): void;

    generatePDF(
        id: string,
        withSignature: boolean,
        markAsPrint?: boolean,
        attachedDocuments?: ExportableFileModel[],
        success?: (file: ExportableFile, patientClinicianModel: IPatientCliniciansModel, attachedDocuments?: ExportableFile[]) => void,
        error?: (message: ErrorMessage[]) => void,
        faxLabRequestComposite?: IFaxLabRequestComposite
    ): void;

    markAsPrinted(
        id: string,
        success?: () => void,
        error?: (message: ErrorMessage[]) => void
    ): void;

    fax(
        faxGenericPDFComposite: BaseFaxMedicalRequestComposite,
        success?: () => void,
        error?: (message: ErrorMessage[]) => void
    ): void;

    createExportableFile(
        id: string
    ): ExportableFile;
}

class ConsultationRequestAction implements IMedicalRequestAction {
    private static _instance: ConsultationRequestAction = null;

    public static getInstance(): ConsultationRequestAction {
        if (this._instance == null)
            this._instance = new ConsultationRequestAction();

        return this._instance;
    }

    public async load(id: Guid, includePatientInfo?: boolean, success?: MedicalRequestActionLoadedCallback, error?: (message: ErrorMessage[]) => void): Promise<void> {
        const requestData = {
            RequestId: id,
            IncludePatientInfo: includePatientInfo
        };

        const response = await Client.postAsync<LoadConsultationResponse>(
            ServerOperations.Server.Myle_LoadSelectedConsultationRequest,
            requestData
        );

        if (!response.isSuccess) {
            error?.(response.errors);
            return;
        }

        const item = ConsultationRequest.fromComposite(response.data.ConsultationRequest);
        item.comments = response.data.Comments;
        item.signedPrintAuthorizations = response.data.SignedPrintAuthorization ?? [];
        const patient = response.data.PatientInfo && isArrayNullOrUndefinedOrEmpty(response.data.PatientInfo.Errors) ? Patient.FromResponseData(response.data.PatientInfo) : null;
        const patientPrintInfo = PrintInfo.fromConsultationRequestInboxItem(response.data.ConsultationRequest, patient);

        success?.(item, patientPrintInfo, response.data.LinkedCareElement && new MedicalItemScreeningTestWithLink(response.data.LinkedCareElement), response.data.AttachedObjects.map(x => ExportableFileHelper.fromAttachedObject(x).toExportableFileModel(ExportableFileDisplayOption.NAME)));
    }

    public async save(
        item: ConsultationRequest,
        linkingRequest?: LinkingCareElementRequest,
        comment?: string,
        shouldNotUpdateMedicalRequest: boolean = false,
        patientAttachments: AttachedObjectResponse[] = [],
        success?: (response: ConsultationRequest, shouldSaveRelatedEmployee: boolean, printInfo?: PrintInfo) => void,
        error?: (message: ErrorMessage[]) => void
    ): Promise<void> {
        const requestData = {
            ConsultationRequest: item.toJSON(),
            Comment: comment,
            ElementToLink: linkingRequest ? linkingRequest.toLinkElement : null,
            PreExistingLink: linkingRequest ? linkingRequest.preExistingLink : false,
            EmrTemplateIdToUnlink: linkingRequest ? linkingRequest.emrTemplateIdToUnlink : null,
            ShouldNotUpdateMedicalRequest: shouldNotUpdateMedicalRequest,
            PatientAttachments: patientAttachments
        };

        const response = await Client.postAsync<SaveConsultationResponse>(
            ServerOperations.Server.Myle_Patient_MedicalNote_UpsertConsultationRequest,
            requestData
        );

        if (!response.isSuccess) {
            error?.(response.errors);
            return;
        }

        const consultationResponse = ConsultationRequest.fromComposite(response.data.ConsultationRequest);
        const printInfo = PrintInfo.fromConsultationRequestInboxItem(response.data.ConsultationRequest);
        success?.(consultationResponse, response.data.ShouldSaveRelatedEmployee, printInfo);
    }

    public delete(consultationRequestId: Guid, encounterId: Guid, success?: (requestId: Guid, encounterId: Guid) => void, error?: (message: ErrorMessage[]) => void): void {
        const requestData = {
            ConsultationRequestId: consultationRequestId,
            encounterId: encounterId
        };

        Client.Postv2(
            ServerOperations.Server.Myle_DeleteConsultationRequest,
            requestData,
            () => {
                success && success(consultationRequestId, encounterId);
            },
            errorMsgs => error && error(errorMsgs)
        );
    }

    public invalidate(id: Guid, encounterId: Guid, isInvalid: boolean, sharedItemId: string, success?: (id: Guid, encounterId: Guid, isInvalid: boolean) => void, error?: (message: ErrorMessage[]) => void): void {
        const requestData = {
            MedicalRequestId: id,
            EncounterId: encounterId,
            MedicalRequestType: MedicalRequestType.ConsultationRequest,
            IsInvalid: isInvalid,
            SharedItemId: sharedItemId
        };

        Client.Postv2(
            ServerOperations.Server.Myle_Patient_MedicalNote_ToggleIsInvalidMedicalRequest,
            requestData,
            response => success && success(response.MedicalRequestId, encounterId, response.IsInvalid),
            errorMsgs => error && error(errorMsgs)
        );
    }

    public async generatePDF(
        id: string,
        withSignature: boolean,
        markAsPrint: boolean = false,
        attachedDocuments?: ExportableFileModel[],
        success?: (file: ExportableFile, patientClinicianModel: IPatientCliniciansModel, attachedDocuments?: ExportableFile[]) => void,
        error?: (message: ErrorMessage[]) => void
    ): Promise<void> {
        const response = await Client.postAsync<{ PatientInfo: LoadPatientModel }>(
            ServerOperations.Server.Myle_PrintConsultationRequest,
            {
                ConsultationRequestId: id,
                WithSignature: withSignature,
                MarkAsPrinted: markAsPrint
            }
        );

        if (!response.isSuccess) {
            error?.(response.errors);
            return;
        }

        const patient = response.data.PatientInfo ? Patient.FromResponseData(response.data.PatientInfo) : null;
        const file = new FileTypeConsultationRequest({ ConsultationRequestId: id, description: null, date: null });
        success?.(
            file,
            patient ? patient.ToPatientCliniciansModel() : null,
            cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments && attachedDocuments?.any()
                ? attachedDocuments.map(p => p.Model)
                : null
        );
    }

    public createExportableFile(
        id: string
    ): ExportableFile {
        return new FileTypeConsultationRequest({ ConsultationRequestId: id, description: null, date: null });
    }

    public markAsPrinted(id: string, success?: () => void, error?: (message: ErrorMessage[]) => void): void {
        Client.Postv2(
            ServerOperations.Server.Myle_MedicalRequest_MarkAsPrinted,
            {
                MedicalRequestId: id,
                MedicalRequestType: MedicalRequestType.ConsultationRequest
            },
            () => success && success(),
            errors => error && error(errors)
        );
    }

    public fax(faxGenericPDFComposite: FaxConsultationRequestComposite, success?: () => void, error?: (message: ErrorMessage[]) => void): void {
        Client.Postv2(
            ServerOperations.Server.Myle_Faxing_SendConsultationRequest,
            {
                FaxConsultationRequestComposite: faxGenericPDFComposite
            },
            (response: GenericResponse) => {
                if (response.IsSuccess)
                    success && success();
                else
                    error && error([new ErrorMessage({ Message: cacheStore.GetString("SendingFaxFailedLabel") })]);
            },
            errors => error && error(errors)
        );
    }
}

class LabRequestAction implements IMedicalRequestAction {
    private static _instance: LabRequestAction = null;

    public static getInstance(): LabRequestAction {
        if (this._instance == null)
            this._instance = new LabRequestAction();

        return this._instance;
    }

    public async load(id: string, includePatientInfo?: boolean, success?: MedicalRequestActionLoadedCallback, error?: (message: ErrorMessage[]) => void): Promise<void> {
        const requestData = {
            RequestId: id,
            IncludePatientInfo: includePatientInfo
        };

        const response = await Client.postAsync<LoadLabResponse>(
            ServerOperations.Server.Myle_LoadSelectedLabRequest,
            requestData
        );

        if (!response.isSuccess) {
            error?.(response.errors);
            return;
        }

        const item = MyleModelsPortfolioLabRequest.fromComposite(response.data.LabRequest);
        item.comments = response.data.Comments;
        item.signedPrintAuthorizations = response.data.SignedPrintAuthorization ?? [];
        const patient = response.data.PatientInfo && isArrayNullOrUndefinedOrEmpty(response.data.PatientInfo.Errors) ? Patient.FromResponseData(response.data.PatientInfo) : null;
        const patientInfo = PrintInfo.fromLabRequestInboxItem(response.data.LabRequest, patient);

        success?.(
            item,
            patientInfo,
            response.data.LinkedCareElement && new MedicalItemScreeningTestWithLink(response.data.LinkedCareElement),
            response.data.AttachedObjects.map(x => ExportableFileHelper.fromAttachedObject(x).toExportableFileModel(ExportableFileDisplayOption.NAME))
        );
    }

    public async save(
        item: MyleModelsPortfolioLabRequest,
        linkingRequest?: LinkingCareElementRequest,
        comment?: string,
        shouldNotUpdateMedicalRequest: boolean = false,
        patientAttachments: AttachedObjectResponse[] = [],
        success?: (response: MyleModelsPortfolioLabRequest, shouldSaveRelatedEmployee: boolean, printInfo?: PrintInfo) => void,
        error?: (message: ErrorMessage[]) => void
    ): Promise<void> {
        const requestData = {
            LabRequest: item,
            LabRequestBillCodes: item.billCodes,
            Comment: comment,
            ElementToLink: linkingRequest ? linkingRequest.toLinkElement : null,
            PreExistingLink: linkingRequest ? linkingRequest.preExistingLink : false,
            EmrTemplateIdToUnlink: linkingRequest ? linkingRequest.emrTemplateIdToUnlink : null,
            ShouldNotUpdateMedicalRequest: shouldNotUpdateMedicalRequest,
            PatientAttachments: patientAttachments
        };

        const response = await Client.postAsync<{ LabRequest: RequestsInboxItem, ShouldSaveRelatedEmployee: boolean }>(
            ServerOperations.Server.Myle_Patient_MedicalNote_AddLabRequest,
            requestData
        );

        if (!response.isSuccess) {
            error?.(response.errors);
            return;
        }

        const newItem = MyleModelsPortfolioLabRequest.fromComposite(response.data.LabRequest);
        const printInfo = PrintInfo.fromLabRequestInboxItem(response.data.LabRequest);
        success?.(newItem, response.data.ShouldSaveRelatedEmployee, printInfo);
    }

    public delete(RequestId: Guid, encounterId: Guid, success?: (requestId: Guid, encounterId: Guid) => void, error?: (message: ErrorMessage[]) => void): void {
        const requestData = {
            RequestId: RequestId,
            encounterId: encounterId
        };

        Client.Postv2(
            ServerOperations.Server.Myle_DeleteLabRequest,
            requestData,
            () => {
                success && success(RequestId, encounterId);
            },
            errorMsgs => error && error(errorMsgs)
        );
    }

    public send(item: MyleModelsPortfolioLabRequest, success?: (SendStatus: string) => void, error?: (message: ErrorMessage[]) => void): void {
        if (item.type === LabRequestType.None || !cacheStore.IsApexInterfaceActive())
            return;

        const operation = item.type === LabRequestType.RIS ? ServerOperations.Server.Myle_ChristusRISProxySendRequestRIS : ServerOperations.Server.Myle_ChristusLISProxySendRequestLIS;
        const requestData = {
            requestId: item.id
        };

        Client.Postv2(
            operation,
            requestData,
            response => {
                if (response.Success)
                    success && success(response.SendStatus);
                else // RIS Error.
                    sessionStore.showPopup(response.InterfaceResponse || "Unknown Error");
            },
            erroMsgs => error && error(erroMsgs)
        );
    }

    public invalidate(id: Guid, encounterId: Guid, isInvalid: boolean, sharedItemId?: string, success?: (id: Guid, encounterId: Guid, isInvalid: boolean) => void, error?: (message: ErrorMessage[]) => void): void {
        const requestData = {
            MedicalRequestId: id,
            EncounterId: encounterId,
            MedicalRequestType: MedicalRequestType.LabRequest,
            IsInvalid: isInvalid,
            SharedItemId: sharedItemId
        };

        Client.Postv2(
            ServerOperations.Server.Myle_Patient_MedicalNote_ToggleIsInvalidMedicalRequest,
            requestData,
            response => success && success(response.MedicalRequestId, encounterId, response.IsInvalid),
            errorMsgs => error && error(errorMsgs)
        );
    }

    public async generatePDF(
        id: string,
        withSignature: boolean,
        markAsPrint: boolean = false,
        attachedDocuments?: ExportableFileModel[],
        success?: (file: ExportableFile, patientClinicianModel: IPatientCliniciansModel, attachedDocuments?: ExportableFile[]) => void,
        error?: (message: ErrorMessage[]) => void,
        faxLabRequestComposite: IFaxLabRequestComposite = null
    ): Promise<void> {
        const response = await Client.postAsync<{ PatientInfo: LoadPatientModel }>(
            ServerOperations.Server.Myle_PrintSelectedRequest,
            {
                RequestId: id,
                AddSignature: withSignature,
                MarkAsPrinted: markAsPrint,
                FaxLabRequestComposite: faxLabRequestComposite
            }
        );

        if (!response.isSuccess) {
            error?.(response.errors);
            return;
        }

        const patient = response.data.PatientInfo ? Patient.FromResponseData(response.data.PatientInfo) : null;
        const file = new FileTypeExamRequest({ ExamRequestId: id, description: null, date: null });
        success?.(
            file,
            patient ? patient.ToPatientCliniciansModel() : null,
            cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments && attachedDocuments?.any()
                ? attachedDocuments.map(p => p.Model)
                : null
        );
    }

    public createExportableFile(
        id: string
    ): ExportableFile {
        return new FileTypeExamRequest({ ExamRequestId: id, description: null, date: null });
    }

    public markAsPrinted(id: string, success?: () => void, error?: (message: ErrorMessage[]) => void): void {
        Client.Postv2(
            ServerOperations.Server.Myle_MedicalRequest_MarkAsPrinted,
            {
                MedicalRequestId: id,
                MedicalRequestType: MedicalRequestType.LabRequest
            },
            () => success && success(),
            errors => error && error(errors)
        );
    }

    public fax(faxGenericPDFComposite: FaxLabRequestComposite, success?: () => void, error?: (message: ErrorMessage[]) => void): void {
        Client.Postv2(
            ServerOperations.Server.Myle_Faxing_SendLabRequest,
            {
                FaxLabRequestComposite: faxGenericPDFComposite
            },
            (response: GenericResponse) => {
                if (response.IsSuccess)
                    success && success();
                else
                    error && error([new ErrorMessage({ Message: cacheStore.GetString("SendingFaxFailedLabel") })]);
            },
            errors => error && error(errors)
        );
    }
}

class LinkedCareElementsAction {
    private static _instance: LinkedCareElementsAction = null;

    public static getInstance(): LinkedCareElementsAction {
        if (!this._instance)
            this._instance = new LinkedCareElementsAction();

        return this._instance;
    }

    public loadAvailableCareElements(encounterId: string, success?: (elements: MedicalItemScreeningTest[]) => void, error?: (message: ErrorMessage[]) => void): void {
        const requestData = {
            EncounterId: encounterId
        };

        Client.Postv2(
            ServerOperations.Server.Myle_LoadAvailableLinkedCareElements,
            requestData,
            response => {
                if (response.AvailableElements) {
                    success && success(response.AvailableElements.map(x => new MedicalItemScreeningTest(x)));
                }
            },
            errorMsgs => error && error(errorMsgs)
        );
    }
}

class LinkingCareElementRequest {
    private _toLinkElement: MedicalItemScreeningTestResponse;
    private _preExistingLink: boolean;
    private _emrTemplateIdToUnlink: string;

    constructor(linkedElement: MedicalItemScreeningTest, toLinkElement: MedicalItemScreeningTest) {
        if (!linkedElement && !toLinkElement)
            return;

        this._toLinkElement = toLinkElement ? toLinkElement.model : null;
        this._emrTemplateIdToUnlink = linkedElement ? linkedElement.model.EMRTemplateId : null;

        this._preExistingLink = toLinkElement && linkedElement && toLinkElement.model.MedicalItemId === linkedElement.model.MedicalItemId;
        if (this._preExistingLink)
            this._emrTemplateIdToUnlink = "";
    }

    public get toLinkElement(): MedicalItemScreeningTestResponse {
        return this._toLinkElement;
    }

    public get emrTemplateIdToUnlink(): string {
        return this._emrTemplateIdToUnlink;
    }

    public get preExistingLink(): boolean {
        return !!this._preExistingLink;
    }
}

export { ConsultationRequestAction, IMedicalRequestAction, LabRequestAction, LinkedCareElementsAction, LinkingCareElementRequest, MedicalRequestActionLoadedCallback };
